import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StatusFilterTree, TextFilter, TimerFilter } from "../filter";
import { ItemNamesMap, PricingListType } from "../../../../constants/pricing";
import { Button, Drawer, Popover, Space, Table, Tag } from "antd";
import { getUser } from "../../../../states/user/user.reducer";
import { FilterFilled, SyncOutlined } from "@ant-design/icons";
import QrCode from "../../../../components/common/qrcode";
import { CarSizeTree, SaveTypeTree } from "../const";
import { Page } from "../../../../constants/common";
import { ColumnsType } from "antd/es/table"; 
import Requestor from "../../../../requestor";
import { useSelector } from "react-redux";
import debounce from "lodash.debounce";
import PricingDetail from "../detail";
import QS from "query-string";
import moment from "moment";

// 序号对应存储该筛选条件的位置
const FilterItemkeyList: string[] = ['outNo', "csize", 'mode', 'type', 'providerId', "start", "page", "size"];
const FilterItemIndexMap = new Map<string, number>(FilterItemkeyList.map((key, index) => ([key, index]))); //方便查询每个筛选条件对应的索引
type FilterItem = { name: string, values: (string | number)[], reset: (() => void) | undefined }

const TableList:React.FC<{}> = () => {

  const [listData, setlistData] = useState<Page<PricingListType>>({ page: 1, size: 0, total: 0, payload: [] });
  const [loading, setLoading] = useState<boolean>(false);
  const [filterParams, setFillterParams] = useState<any>();
  const filters = useRef<(FilterItem | undefined)[]>([]);  //存储序号和FilterItemIndexMap对应
  const [showSid, setShowSid] = useState<string | null>(null);
  const User = useSelector(getUser);

  useEffect(() => {
    getPricngListData();
  }, []);

  const getPricngListData = useCallback(
    async (page = 1, size = 15) => {
      const params: any = { page, size, providerId: User?.companyId };
      let wheres = filters.current.filter(f => f && f.name && f.values && f.values.length > 0);
      setFillterParams(params);
      wheres.forEach(el => {
        if (el?.name == "start") {
          params["start"] = el?.values[0];
          params["end"] = el?.values[1];
        }
        if (el) params[el.name] = el?.values[0];
      });
      const paramsStr = QS.stringify(params);
      try {
        setLoading(true);
        const res = await Requestor.getPriceList<Page<PricingListType>>(paramsStr);
        if (res.error !== "") {
          throw new Error(res.error);
        }
        setlistData(res.result);
        setLoading(false);
      } catch(err) {
        setLoading(false);
        // notification.error({ message: err })
      };
  }, [])

   /**
   * 表格筛选条件变化的处理
   * @param  key 筛选项name
   * @param  values 筛选的values
   */
   const onFilterChange = useCallback((key: string, values: (string | number)[], needUpdate: boolean = true) => {

    let index = FilterItemIndexMap.get(key);
    if (index === undefined) return;

    if (values.length < 1) {
      filters.current[index] = undefined;
      if (needUpdate) getPricngListData(); 
      return;
    };

    filters.current[index] = { name: key, values: values, reset: filters.current[index]?.reset };
    if (needUpdate) {
      getPricngListData();
    }
  }, []);

  const registerReset = useCallback((key: string, fun: () => void) => {
    let index = FilterItemIndexMap.get(key);
    if (index === undefined) return;

    let ori = filters.current[index];
    filters.current[index] = { name: ori?.name || '', values: ori?.values || [], reset: fun }
  }, []);
  
  const columns: ColumnsType<PricingListType> = useMemo(() => {
    const Icon = (mode: string) => {
      return <FilterFilled style={{ color: filterParams?.[mode] !== undefined ? "#40a9ff" : "gray" }} />;
    }
    return [
      {
        title: "序号",
        dataIndex: "createdAt",
        width: 20,
        render: (_v, _r, index) => {
          return <Tag color="default" >{index + 1}</Tag>
        },
      },
      {
        title: "报案号",
        dataIndex: "outNo",
        width: 60,
        filterIcon: Icon('outNo'),
        filterDropdown: () => {
          return (
            <TextFilter 
              skey="outNo" 
              label="报案号" 
              registerReset={registerReset.bind(this, 'outNo')}
              onChange={val => onFilterChange('outNo', val)} 
            />
          );
        },
      },
      {
        title: "车型大小",
        dataIndex: "size",
        filterIcon: Icon('csize'),
        width: 80, 
        filterDropdown: () => (
          <StatusFilterTree
            multiple={false}
            registerReset={registerReset.bind(this, 'csize')}
            treeData={CarSizeTree} 
            onChange={(val) => onFilterChange('csize', val)} 
          />
        ),
        render: (val: number) => {
          return CarSizeTree.map(el => el.title)[val ||  0]; 
        }
      },
      {
        title: "救援模式",
        dataIndex: "mode",
        width: 80,
        render: (val: number) => val === 1 ? '非事故救援' : '事故救援',
      },
      {
        title: "救援类型",
        dataIndex: "type",
        filterIcon: Icon('type'),
        width: 80,
        render: (val: number) => val === 1 ? "商用车" : "乘用车",
        filterDropdown: () => (
          <StatusFilterTree
            multiple={false}
            treeData={SaveTypeTree} 
            registerReset={registerReset.bind(this, 'type')}
            onChange={(val) => onFilterChange('type', val)} 
          />
        ),
      },
      {
        title: "查勘员",
        dataIndex: "surveyorName",
        width: 100,
      },
      {
        title: "当地报价",
        dataIndex: "localFee",
        width: 30,
        render: (val: number) => <span>￥{val / 100}</span>
      },
      {
        title: "核价总计",
        dataIndex: "totalFee",
        width: 80,
        render: (val: number, record) => {
          const itemNames = Object.keys(record.items).map((el: string) => ItemNamesMap[el]);
          if (!itemNames!.length) return <span>￥{val / 100 }</span>;
          return <Popover content={itemNames.join("; ")}><span style={{ cursor: "pointer" }}>￥{val / 100 }</span></Popover>;
        },
      },
      {
        title: "核价时间",
        dataIndex: "createdAt",
        width: 140,
        filterIcon: Icon('start'),
        render: (time: number, record) => {
          return moment(time * 1000).format("YYYY-MM-DD HH:mm")
        },
        filterDropdown: () => (
          <TimerFilter 
            registerReset={registerReset.bind(this, 'start')} 
            onChange={(times) => {
              if (times.length === 2) {
                const start = times[0].startOf("day").format("X");
                const end = times[1].endOf("day").format("X");
                onFilterChange('start', [start, end]);
              } else {
                onFilterChange('start', [])
              }
          }}/>
        ),
      },
      {
        title: "操作",
        dataIndex: "createdAt",
        width: 80,
        render: (_, record) => {
          return (
            <Space size="middle">
              <a onClick={() => setShowSid(record.sid)}>详情</a>
              <QrCode 
                license={"核价详情"}
                key={"qcode-" + record.sid} 
                url={`https://dispatch.open.trailerman.cn/pricing?sid=${record.sid}`} 
              />
            </Space>
          );
        }
      },

    ]
  }, [filterParams]);

  return (
    <div>
      <div style={{ textAlign: "right", marginBottom: 16 }}>
        <Button
          key="ref--"
          type="primary"
          icon={<SyncOutlined />}
          onClick={debounce(() => getPricngListData(), 500)}
        >刷新</Button>
      </div>
      <Table
        rowKey={"id"}
        columns={columns}
        dataSource={listData.payload}
        loading={loading}
        pagination={{
          current: listData.page,
          pageSize: listData.size,
          total: listData.total,
          onChange: (page, size) => getPricngListData(page, size),
          showSizeChanger: true,
          showTotal: (total: number, range: [number, number]) =>
            `共${total}条记录，当前第${range[0]}-${range[1]}条`,
        }}
      >
      </Table>
      <Drawer 
        title="核价详情" 
        placement="right" 
        onClose={() => setShowSid(null)} 
        open={!!showSid}
        width={740}
      >
        {showSid && <PricingDetail sid={showSid}/>}
      </Drawer>
    </div>
  );
};

export default TableList;